/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import Monster from 'model/monster/Monster';
import ObjectHelper from 'utils/ObjectHelper';

/**
 * Shared monster save/export/hash helpers. Previously the "prep -> removeEmpty -> sortObjectKeys ->
 * stringify" pipeline (and the change-detection hash) were copy-pasted across MonsterContainer,
 * DownloadJsonButton and DownloadAllMonstersButton; keeping one source of truth here prevents the
 * copies from drifting (which would silently break unsaved-changes detection).
 */

/**
 * Clone a monster for saving/exporting, clearing the derived npcData spell arrays (they are re-derived
 * from the spell slug lists on load, so persisting them just bloats the file and destabilizes hashes).
 * The input is deep-cloned, so the caller's monster is never mutated.
 */
export const prepMonsterForSave = (m: Monster): Monster => {
  const copy = structuredClone(m);
  if (copy.npcData) {
    copy.npcData.spellbookSpellsArrayBySpellLevelPrimary = [];
    copy.npcData.spellbookSpellsArrayBySpellLevelSecondary = [];
    copy.npcData.spellsPreparedArrayBySpellLevelPrimary = [];
    copy.npcData.spellsPreparedArrayBySpellLevelSecondary = [];
  }
  return copy;
};

/** The canonical save shape: strip empty values, then sort keys (stable output for save/export/hash). */
export const toSortedSaveObject = (data: Record<string, any>): Record<string, any> => {
  const cleaned = ObjectHelper.removeEmpty(data);
  return ObjectHelper.sortObjectKeys(cleaned);
};

/** 32-bit rolling string hash, returned as a string. */
export const hashString = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString();
};

/** Stable hash of a monster's saved form, used to detect unsaved changes. */
export const generateMonsterHash = (monster: Monster): string => {
  try {
    const sorted = toSortedSaveObject(prepMonsterForSave(monster) as unknown as Record<string, any>);
    return hashString(JSON.stringify(sorted));
  } catch (error) {
    console.error('Error generating monster hash:', error);
    return '';
  }
};
